import { ConditionBuilder } from "../components/shared/ConditionBuilder";
import { RewardBuilder } from "../components/shared/RewardBuilder";
import { useState, useEffect } from "react";
import { DeleteDialog } from "../components/shared/DeleteDialog";
import {
    HiChevronLeft,
    HiOutlineDocumentText,
    HiOutlineLightningBolt,
    HiOutlineShieldCheck,
    HiOutlineChartBar,
    HiOutlinePlay,
    HiOutlinePause,
    HiOutlineCheck,
    HiOutlineSave,
    HiOutlineCalendar,
    HiOutlineClock,
    HiOutlineTrash
} from "react-icons/hi";
import {
    DROPDOWN_TYPE_CONDITION_BASE,
    DROPDOWN_TYPE_CONDITION_OPERATOR, DROPDOWN_TYPE_METRIC,
    DROPDOWN_TYPE_REWARD_BASE, DROPDOWN_TYPE_REWARD_OPERATOR
} from "../components/shared/Dropdow";
import { useDispatch } from "react-redux";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
    deleteCampaignAction,
    submitAction,
    updateCampaignAction
} from "../redux/actions/campaignActions";
import { Snackbar } from "../components/shared/Snackbar";
import { CampaignMenu } from "../components/shared/CampaignMenu";
import { SubmissionMenu } from "../components/shared/SubmissionMenu";
import { FraudPreventionBuilder } from "../components/shared/FraudPreventionBuilder";
import {
    FRAUD_PREVENTION_MAX_POINTS,
    FRAUD_PREVENTION_MAX_TRANSACTIONS
} from "../components/modals/CampaignBuilderModal";
import { Transition } from "@headlessui/react";
import Button from "../components/shared/Button";
import Card from "../components/shared/Card";
import Badge from "../components/shared/Badge";
import { cn } from "../utils/cn";
import { getEffectiveStatus } from "../utils/campaignStatus";

export const CONDITION_VALUE = "condition-value";
export const REWARD_POINTS = "reward-points";
export const FRAUD_PREVENTION = "fraud-prevention";

const STATUS_BADGE_VARIANTS = {
    running: "success",
    paused: "warning",
    completed: "default",
    scheduled: "dark",
};

export const SingleCampaign = (props) => {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [newCampaignData, setNewCampaignData] = useState(props.campaign)
    const [missingValue, setMissingValue] = useState(null)
    const [publishing, setPublishing] = useState(false)
    const [showTestSubmissionSucceeded, setShowTestSubmissionSucceeded] = useState(false)
    const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
    const [message, showMessage] = useState("")
    const [activeSection, setActiveSection] = useState("conditions");
    const [isEdited, setIsEdited] = useState(false);
    const [animateSave, setAnimateSave] = useState(false);
    const dispatch = useDispatch();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        setNewCampaignData(props.campaign);
    }, [props.campaign]);

    useEffect(() => {
        setIsEdited(hasDataChanged());
    }, [newCampaignData]);

    const setSelected = (conditionRewardId, type, dropdownValue) => {
        const updatedData = { ...newCampaignData };

        if (type === DROPDOWN_TYPE_CONDITION_BASE) {
            updatedData.conditions.find(condition => condition.id === conditionRewardId).transactionBase = dropdownValue;
        } else if (type === DROPDOWN_TYPE_CONDITION_OPERATOR) {
            updatedData.conditions.find(condition => condition.id === conditionRewardId).transactionOperator = dropdownValue;
        } else if (type === DROPDOWN_TYPE_METRIC) {
            updatedData.conditions.find(condition => condition.id === conditionRewardId).valueMetric = dropdownValue;
        } else if (type === DROPDOWN_TYPE_REWARD_BASE) {
            updatedData.rewards.find(reward => reward.id === conditionRewardId).rewardBase = dropdownValue;
        } else if (type === DROPDOWN_TYPE_REWARD_OPERATOR) {
            updatedData.rewards.find(reward => reward.id === conditionRewardId).operator = dropdownValue;
        }

        setNewCampaignData(updatedData);
    }

    const handlePublish = async () => {
        setPublishing(true);
        try {
            await dispatch(updateCampaignAction(newCampaignData));
            setAnimateSave(true);
            setTimeout(() => {
                setAnimateSave(false);
                setIsEdited(false);
            }, 1500);
        } catch (error) {
            showMessage("Error updating campaign");
        } finally {
            setPublishing(false);
        }
    }

    const handleDeletion = async () => {
        setIsDeleting(true);
        try {
            await dispatch(deleteCampaignAction(props.campaign._id));
            setShowDeleteDialog(false);
            props.onClose();
        } finally {
            setIsDeleting(false);
        }
    }

    const showDialog = () => {
        setShowDeleteDialog(true)
    }

    const hideDialog = () => {
        setShowDeleteDialog(false)
    }

    const changeStatus = () => {
        const updatedData = { ...newCampaignData };
        updatedData.status = updatedData.status === "running" ? "paused" : "running";
        setNewCampaignData(updatedData);
    }

    const onInputChange = (id, type, value) => {
        try {
            const realValue = value < 0 ? 0 : value;
            const updatedData = { ...newCampaignData };

            if (type === CONDITION_VALUE) {
                updatedData.conditions.find(condition => condition.id === id).inputValue = realValue;
            } else if (type === REWARD_POINTS) {
                updatedData.rewards.find(reward => reward.id === id).points = realValue;
            } else if (type === FRAUD_PREVENTION) {
                if (id === FRAUD_PREVENTION_MAX_POINTS) {
                    updatedData.fraudPrevention.maxPoints = realValue;
                } else if (id === FRAUD_PREVENTION_MAX_TRANSACTIONS) {
                    updatedData.fraudPrevention.maxTransactions = realValue;
                }
            }

            setNewCampaignData(updatedData);
        } catch (e) {
            console.error(e);
        }
    }

    const hasDataChanged = () => {
        if (newCampaignData.name !== props.campaign.name) {
            return true
        }

        for (let newCondition of newCampaignData.conditions) {
            const initialCondition = props.campaign.conditions.find(initialCondition => initialCondition.id === newCondition.id);
            if (initialCondition && ((initialCondition.inputValue !== newCondition.inputValue) || (initialCondition.transactionBase !== newCondition.transactionBase) || (initialCondition.transactionOperator !== newCondition.transactionOperator))) {
                return true
            }
        }

        for (let newReward of newCampaignData.rewards) {
            const initialReward = props.campaign.rewards.find(initialReward => initialReward.id === newReward.id);
            if (initialReward && ((initialReward.points !== newReward.points) || (initialReward.rewardBase !== newReward.rewardBase) || (initialReward.operator !== newReward.operator))) {
                return true
            }
        }

        if (newCampaignData.status !== props.campaign.status) {
            return true;
        }

        return false;
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case "running":
                return <HiOutlinePlay className="w-4 h-4 mr-1.5" />;
            case "paused":
                return <HiOutlinePause className="w-4 h-4 mr-1.5" />;
            case "completed":
                return <HiOutlineCheck className="w-4 h-4 mr-1.5" />;
            default:
                return null;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Not set';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const tabClass = (sectionKey) =>
        cn(
            "py-3 px-1 font-medium text-body-md flex items-center transition-colors duration-150 border-b-2",
            activeSection === sectionKey
                ? "text-ink border-ink"
                : "text-muted border-transparent hover:text-ink"
        );

    return (
        <div className="pb-5 flex flex-col w-full max-w-5xl mx-auto px-4">
            {showTestSubmissionSucceeded ? (
                <Snackbar onClose={() => setShowTestSubmissionSucceeded(false)} text="Test submission sent!" successIcon hideCloseButton/>
            ) : null}
            {showSubmissionDialog ? (
                <SubmissionMenu send={async (identification, name, value) => {
                    await dispatch(submitAction({campaignId: props.campaign._id, identification, name, value}))
                    setShowSubmissionDialog(false)
                    setShowTestSubmissionSucceeded(true);
                }}/>
            ) : null}
            {message.length > 0 ? (
                <Snackbar onClose={() => showMessage("")} text={message} hideCloseButton/>
            ) : null}

            {/* Header Section */}
            <div className="w-full mt-6 mb-6">
                <div className="flex flex-row justify-between items-start mb-6">
                    <div className="flex items-center">
                        <button
                            onClick={props.onClose}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-ink hover:bg-surface-soft transition-colors"
                            aria-label="Back"
                            data-help-title="Back to campaigns"
                            data-help="Returns to the campaign grid. Any unsaved edits to this campaign are discarded."
                        >
                            <HiChevronLeft className="w-6 h-6"/>
                        </button>
                        <h5 className="ml-3 text-display-md text-ink" data-help-title="Campaigns" data-help="Shows the name of the campaign you are viewing. Campaigns are the rules that turn customer activity into loyalty points: when members earn, how many points they get, and the limits that keep things fair.">{newCampaignData.name}</h5>
                    </div>
                    <div className="flex items-center space-x-3">
                        {(() => {
                            const effective = getEffectiveStatus(newCampaignData);
                            // Manual paused/running toggle is only meaningful while the
                            // campaign is in its active window. Outside it, show the
                            // derived state (scheduled/completed) without a toggle.
                            const canToggle = effective === "running" || effective === "paused";
                            const badge = (
                                <Badge variant={STATUS_BADGE_VARIANTS[effective] || "default"} data-help-title="Campaign status" data-help="Shows whether the campaign is Scheduled, Running, Paused, or Completed. While the campaign is inside its active dates, admins can click this badge to pause or resume it; the change applies after pressing Save Changes.">
                                    <span className="inline-flex items-center">
                                        {getStatusIcon(effective)}
                                        {effective.charAt(0).toUpperCase() + effective.slice(1)}
                                    </span>
                                </Badge>
                            );
                            return isAdmin && canToggle ? (
                                <button onClick={changeStatus} className="inline-flex items-center" type="button">
                                    {badge}
                                </button>
                            ) : badge;
                        })()}
                        <CampaignMenu showMessage={showMessage} showSubmissionDialog={showSubmissionDialog} setShowSubmissionDialog={setShowSubmissionDialog} campaign={props.campaign}/>
                    </div>
                </div>

                {/* Campaign Duration Info */}
                <Card variant="surface-soft" className="p-6" data-help-title="Campaign duration" data-help="Shows when the campaign starts and ends and how many member submissions it has received. The campaign only awards points inside this date window.">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center">
                            <HiOutlineCalendar className="w-5 h-5 mr-3 text-ink" />
                            <div>
                                <p className="text-body-md font-medium text-ink">Start Date</p>
                                <p className="text-body-md text-muted">{formatDateTime(newCampaignData.startDate)}</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <HiOutlineClock className="w-5 h-5 mr-3 text-ink" />
                            <div>
                                <p className="text-body-md font-medium text-ink">End Date</p>
                                <p className="text-body-md text-muted">{formatDateTime(newCampaignData.endDate)}</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <HiOutlineChartBar className="w-5 h-5 mr-3 text-ink" />
                            <div>
                                <p className="text-body-md font-medium text-ink">Total Submissions</p>
                                <p className="text-body-md text-muted">{(newCampaignData.submissions || []).length}</p>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            <Transition
                show={showDeleteDialog}
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <DeleteDialog noFunction={hideDialog} yesFunction={handleDeletion} isLoading={isDeleting} />
            </Transition>

            {/* Navigation tabs */}
            <div className="border-b border-hairline mb-6">
                <nav className="flex space-x-8" aria-label="Campaign sections">
                    <button
                        type="button"
                        className={tabClass("conditions")}
                        onClick={() => setActiveSection("conditions")}
                        data-help-title="Conditions tab"
                        data-help="Shows the conditions a customer must meet to earn from this campaign, such as spending a minimum amount. All conditions must be met for the reward to apply."
                    >
                        <HiOutlineDocumentText className="w-5 h-5 mr-2" />
                        Conditions
                    </button>
                    <button
                        type="button"
                        className={tabClass("rewards")}
                        onClick={() => setActiveSection("rewards")}
                        data-help-title="Rewards tab"
                        data-help="Shows how points are calculated when the conditions are met: a formula that combines a base value, such as the transaction value or recent activity totals, with a points number."
                    >
                        <HiOutlineLightningBolt className="w-5 h-5 mr-2" />
                        Rewards
                    </button>
                    <button
                        type="button"
                        className={tabClass("fraud")}
                        onClick={() => setActiveSection("fraud")}
                        data-help-title="Fraud prevention tab"
                        data-help="Shows the limits that protect the program from abuse: the maximum points a single member can earn from this campaign and the maximum number of qualifying transactions per member."
                    >
                        <HiOutlineShieldCheck className="w-5 h-5 mr-2" />
                        Fraud Prevention
                    </button>
                </nav>
            </div>

            {/* Active section */}
            <div className="w-full text-left">
                {activeSection === "conditions" && (
                    <div className="space-y-4">
                        {newCampaignData.conditions.map((condition) => (
                            <Card key={condition.id} variant="default">
                                <ConditionBuilder
                                    includeDeleteButton={newCampaignData.conditions.length > 1}
                                    condition={condition}
                                    onDelete={handleDeletion}
                                    onInputChange={onInputChange}
                                    missingValue={missingValue}
                                    setSelected={setSelected}
                                />
                            </Card>
                        ))}
                    </div>
                )}

                {activeSection === "rewards" && (
                    <div className="space-y-4">
                        {newCampaignData.rewards.map((reward) => (
                            <Card key={reward.id} variant="default">
                                <RewardBuilder
                                    reward={reward}
                                    includeDeleteButton={newCampaignData.rewards.length > 1}
                                    onDelete={handleDeletion}
                                    onInputChange={onInputChange}
                                    missingValue={missingValue}
                                    setSelected={setSelected}
                                />
                            </Card>
                        ))}
                    </div>
                )}

                {activeSection === "fraud" && (
                    <Card variant="default">
                        <FraudPreventionBuilder
                            maxPoints={newCampaignData.fraudPrevention.maxPoints}
                            maxTransactions={newCampaignData.fraudPrevention.maxTransactions}
                            onInputChange={onInputChange}
                        />
                    </Card>
                )}
            </div>

            {isAdmin && (
            <div className="flex flex-row justify-between mt-10">
                <Button
                    variant="secondary"
                    onClick={showDialog}
                    leadingIcon={<HiOutlineTrash className="w-5 h-5 text-signature-coral" />}
                    className="text-signature-coral border-signature-coral"
                    data-help-title="Delete campaign"
                    data-help="Opens a confirmation dialog to permanently delete this campaign. Deletion cannot be undone. Only admins can delete campaigns."
                >
                    Delete
                </Button>
                <Button
                    variant="primary"
                    onClick={handlePublish}
                    disabled={!isEdited || publishing}
                    data-help-title="Save changes"
                    data-help="Saves your edits to the campaign, including name, conditions, rewards, and status changes; existing members keep their earned points. Stays disabled until the name, conditions, rewards, or status differ from the saved campaign."
                    leadingIcon={
                        publishing ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : animateSave ? (
                            <HiOutlineCheck className="w-5 h-5" />
                        ) : (
                            <HiOutlineSave className="w-5 h-5" />
                        )
                    }
                >
                    {publishing ? "Saving..." : animateSave ? "Saved!" : (isEdited ? "Save Changes" : "No Changes")}
                </Button>
            </div>
            )}
        </div>
    );
};
